import {
    listOfQuestions,
    GroupTreatmentsStep4Props,
    calculateGroupActionPlanMandatory,
    convertNotPerformToBatchRequest,
    convertNotPerformToUpdateBatchRequestForGroup,
    buildActionPlanListsByNotificationMap,
    mergeFirstNotificationActionPlansFromRedux,
    groupNotificationSysId,
    isNotPerformPlaceholderPlan,
    collectPlaceholderNotPerformSysIdsFromMap,
} from './helpers'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { notificationStatus } from 'constants/notificationStatus'
import { MdOutlineModeEditOutline } from 'react-icons/md'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES_NAMES } from 'constants/name.routes'
import { NotificationAction, NotificationReducer } from 'types/notification'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import { useFieldArray, useForm } from 'react-hook-form'
import ActionTable from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/ActionPlan/Children/ActionTable'
import NotificationActions from 'actions/Notification'
import { Dispatch } from 'redux'
import { toBoolean } from 'helper/string'
import { checkboxStyle, EditButton, Title } from './styles'

/**
 * Determina se o usuário pode editar o plano de ação em grupo
 * corporate_quality_board NÃO pode editar notificações em grupo
 */
export const canEditActionPlan = ({
    loggedInUserIsResponsible,
    isSameLocation,
    isSameSector,
    notificationStatusState
}: {
    loggedInUserIsResponsible: boolean
    isSameLocation: boolean
    isSameSector: boolean
    notificationStatusState: string
}) => {
    if (notificationStatusState === notificationStatus[3].label) {
        return [
            listOfRoles.admin,
            listOfRoles.location_qa
        ]
    } else {
        return [
            ...(loggedInUserIsResponsible ? [listOfRoles.admin] : []),
            ...(isSameLocation && isSameSector && loggedInUserIsResponsible
                ? [listOfRoles.location_manager]
                : []),
            ...(isSameLocation && loggedInUserIsResponsible
                ? [listOfRoles.location_qa]
                : [])
        ]
    }
}

const GroupTreatmentsStep4 = ({
    id,
    tooltipAlert: _tooltipAlertShownInParent,
    currentUser,
    statusIsArchivedOrFinished,
    notificationById,
    notificationStatusState,
    groupData,
    onActionPlanChange,
    loggedInUserIsResponsible = true
}: GroupTreatmentsStep4Props) => {
    const navigate = useNavigate()
    const [disabled, setDisabled] = useState(false);
    const [notAllowed, setNotAllowed] = useState<boolean>(false)

    const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)
    const notificationDispatch: Dispatch<NotificationAction> = useDispatch()

    useEffect(() => {
        // Buscar TODOS os planos de ação da primeira notificação do grupo
        if (groupData?.notifications?.length > 0) {
            const firstNotificationId = groupData.notifications[0]?.sys_id?.value
            if (firstNotificationId) {
                // Buscar todos os planos da primeira notificação
                NotificationActions.getListOfActionPlan(notificationDispatch, firstNotificationId)
            }
        } else {
            // Se não temos dados do grupo ainda, usar busca de grupo como fallback
            NotificationActions.getListOfActionPlanGroup(notificationDispatch, id)
        }
    }, [currentUser, groupData, id])

    useEffect(() => {
        if (!notAllowed) {
            // Priorizar planos individuais sobre planos de grupo
            const actionPlans = notificationSelector?.actionPlans?.length > 0
                ? notificationSelector.actionPlans
                : notificationSelector?.actionPlansGroup || []
            
            if (actionPlans.length > 0) {
                // Verificar se TODOS os planos têm u_do_not_perform_optional = true
                const allPlansAreNotToPerform = actionPlans.every(plan => 
                    toBoolean(plan?.u_do_not_perform_optional)
                )
                setDisabled(allPlansAreNotToPerform)
            } else {
                setDisabled(false)
            }
        }
    }, [notAllowed, notificationSelector?.actionPlans, notificationSelector?.actionPlansGroup])

    // Para grupos, o plano de ação é sempre obrigatório, então não mostrar checkbox
    const hideOrShowCheckUnrealizedOptional = true

    const isActionPlanMandatory = useMemo(() => {
        return calculateGroupActionPlanMandatory(groupData)
    }, [groupData])

    const isSameLocation = useMemo(() => {
        // Para grupos, sempre permitir se o usuário tem acesso à localização
        // ou usar os dados da notificação se disponível
        if (notificationById?.location_incident?.value) {
            return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value).length > 0
        }
        // Se não tem dados da notificação, assumir que pode editar (será validado pelo backend)
        return true
    }, [currentUser, notificationById])

    const isSameSector = useMemo(() => {
        // Para grupos, sempre permitir se o usuário tem acesso ao setor
        // ou usar os dados da notificação se disponível
        if (notificationById?.location_incident?.value && notificationById?.incident_sector?.value) {
            return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value)[0]?.sectors.filter((i) => i.sys_id === notificationById?.incident_sector?.value).length > 0
        }
        // Se não tem dados da notificação, assumir que pode editar (será validado pelo backend)
        return true
    }, [currentUser, notificationById])


    useEffect(() => {
        if (canEditActionPlan({ loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState })
            .includes(currentUser?.profile) && !statusIsArchivedOrFinished) {
            setNotAllowed(false)
        } else {
            setNotAllowed(true)
        }
    }, [loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState, statusIsArchivedOrFinished])

    const redirectToPlanForm = () => {
        navigate(`${ROUTES_NAMES.notifications}/group_action_plan/${id}`)
    }
    const {
        trigger,
        register,
        control,
        formState: { errors },
        getValues,
        reset,
        setValue,
    } = useForm({
        defaultValues: {
            fields: [

            ]
        }
    })
    const { fields } = useFieldArray({
        control,
        name: 'fields'
    })

    const isNotListOfPlaceHolder = () => {
        if (fields.length && fields.length <= 1 && !fields[0].who) {
            return false
        }
        return true
    }
    useEffect(() => {
        // Priorizar planos individuais da primeira notificação sobre planos de grupo
        const individualPlans = notificationSelector?.actionPlans || []
        const groupPlans = notificationSelector?.actionPlansGroup || []

        const actionPlans = individualPlans.length > 0 ? individualPlans : groupPlans
        
        // Verificar se o plano de ação está preenchido e notificar o componente pai
        const isCompleted = actionPlans && actionPlans.length > 0 && actionPlans.some(plan => 
            plan.what && plan.what.trim() !== ''
        )
        onActionPlanChange?.(isCompleted)

        if (actionPlans?.length) {
            const getFieldsData = actionPlans?.map(
                (plan, index) => {
                    const items = listOfQuestions.map((question) => {
                        return {
                            [`${question}`]: plan[question].toString()
                        }
                    })
                    return Object.assign(
                        {
                            sys_id: plan.sys_id,
                            id: plan.sys_id
                        },
                        ...items
                    )
                }
            )
            reset({
                fields: getFieldsData
            })
        } else if ((individualPlans.length === 0) && (groupPlans.length === 0)) {
            reset({
                fields: []
            })
        }
    }, [notificationSelector?.actionPlans, notificationSelector?.actionPlansGroup])

    const onChangeCheckedNotPerform = async (checkedValue: boolean) => {
        try {
            if (!groupData || !currentUser) {
                console.error('Dados do grupo ou usuário não encontrados')
                return
            }

            const plansByNotif = await buildActionPlanListsByNotificationMap(groupData)
            const templatePlansForMerge =
                (notificationSelector.actionPlans?.length
                    ? notificationSelector.actionPlans
                    : notificationSelector.actionPlansGroup) ?? null
            const mergedPlans = mergeFirstNotificationActionPlansFromRedux(
                groupData,
                plansByNotif,
                templatePlansForMerge
            )

            const firstNotificationId = groupNotificationSysId(groupData.notifications[0])
            const templatePlans = firstNotificationId
                ? mergedPlans.get(firstNotificationId) || []
                : []

            if (templatePlans.length > 0) {
                const allFirstNotifArePlaceholders =
                    templatePlans.every(isNotPerformPlaceholderPlan)

                if (!checkedValue && allFirstNotifArePlaceholders) {
                    const toDelete = collectPlaceholderNotPerformSysIdsFromMap(mergedPlans)
                    if (toDelete.length > 0) {
                        await NotificationActions.deleteActionPlanBatch(
                            notificationDispatch,
                            {
                                records: toDelete as any,
                                options: { stopOnError: false },
                            }
                        )
                    }
                } else {
                    const updateBatchRequest = convertNotPerformToUpdateBatchRequestForGroup(
                        templatePlans,
                        mergedPlans,
                        groupData,
                        currentUser,
                        checkedValue
                    )

                    if (updateBatchRequest.records.length > 0) {
                        await NotificationActions.updateActionPlanBatch(
                            notificationDispatch,
                            updateBatchRequest
                        )
                    }
                }
            } else {
                const batchRequest = convertNotPerformToBatchRequest(
                    groupData,
                    currentUser,
                    checkedValue
                )

                await NotificationActions.createActionPlanBatch(notificationDispatch, batchRequest)
            }

            if (groupData?.notifications?.length > 0) {
                const firstNotificationId = groupData.notifications[0]?.sys_id?.value
                if (firstNotificationId) {
                    NotificationActions.getListOfActionPlan(notificationDispatch, firstNotificationId)
                }
            }
            
            NotificationActions.getListOfActionPlanGroup(
                notificationDispatch,
                id
            )
        } catch (error) {
            console.error('Erro ao alterar status "não realizar" em massa:', error)
            // TODO: Implementar feedback de erro visual para o usuário
        }
    };
    return (
        <div
            data-testid="test-group-notification-treatment-step4"
            className='min-h-[500px] px-8'>
            <Title>Plano de Ação</Title>
            <div className="flex flex-row gap-12 mt-[10px] mb-1">
                Ao responder as perguntas, transformamos em ações práticas todo o tratamento da notificação e criamos um checklist que indica as atividades, os prazos e as responsabilidades de todos os envolvidos na resolução dos problemas identificados. O seu propósito é definir as tarefas de forma visual, simples e ágil.
            </div>
            <div className="flex flex-row gap-12 mt-1 mb-10">
                <span className='text-xs text-darkGray'>
                    Preenchimento facultativo no tratamento em grupo.
                </span>
            </div>
            <fieldset disabled={notAllowed}>
                <div className="flex flex-row gap-12 mt-6 mb-10">
                    <EditButton
                        data-testid="test-go-to-group-forum-button"
                        onClick={() => {
                            redirectToPlanForm()
                        }}
                        disabled={disabled || notAllowed}
                        type="button"
                        className={`${disabled || notAllowed ? 'bg-disabled text-textSelect font-normal' : ''}`}
                    >
                        <div className="w-[202px] flex justify-center items-center gap-3">
                            <MdOutlineModeEditOutline size={18} color={`${!disabled && !notAllowed ? 'white' : '#4F4F4F'}`} />
                            <span className="inline-block align-middle">
                                Editar Plano de ação
                            </span>
                        </div>
                    </EditButton>
                    {hideOrShowCheckUnrealizedOptional &&
                        <div data-testid="test-toggle-button" className="mt-2 block min-h-[1.5rem] pl-[1.5rem]">
                            <input
                                onChange={(e) => {
                                    onChangeCheckedNotPerform(e.target.checked)
                                }}
                                checked={disabled}
                                name="u_do_not_perform_optional"
                                className={`${checkboxStyle}  ${notAllowed
                                    ? 'opacity-40'
                                    : 'hover:cursor-pointer'
                                    }`}
                                type="checkbox"
                                id="checkUnrealizedOptional"
                            />
                            <label
                                className={`inline-block pl-[0.15rem]  ${notAllowed
                                    ? 'opacity-40'
                                    : 'hover:cursor-pointer'
                                    }`}
                                htmlFor="checkUnrealizedOptional"
                            >
                                Não realizar o preenchimento
                            </label>
                        </div>
                    }
                </div>
            </fieldset>
            {
                !disabled && fields.length && isNotListOfPlaceHolder() ?
                    <ActionTable
                        trigger={trigger}
                        fields={fields}
                        notAllowed={true}
                        errors={errors}
                        setFocusedInputData={() => { }}
                        register={register}
                        setValue={setValue}
                        control={control}
                        removeField={() => { }}
                        getValues={getValues}
                        isStaticData={true}
                    /> : null
            }
        </div>
    )
}

export { GroupTreatmentsStep4 as GroupActionPlan }